import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Input, Row, Space, Spin, Tree, Typography, message, Tooltip, Tag, Modal } from 'antd';
import { ReloadOutlined, DownOutlined, NodeExpandOutlined, NodeCollapseOutlined } from '@ant-design/icons';
import axios from 'axios';

/**
 * MarketplaceCategoriasTab (JS) — Fluxo por Canal > Categorias
 * - Filtra canais e categorias já vinculados conforme histórico de associação
 * - Quando chegar em folha: pergunta se quer vincular e loga objeto com título do canal
 */

const { Title, Text } = Typography;

const NovaCategoriaMkt = ({ cnpj, voltar, categoriaId }) => {
  // ============================= Constantes ============================= //
  const TOKEN = 'FQNTb2bzbXttAkrGHDS5GSy7lAtjIwx5Yog3tXWwy3s';
  const API_BASE = 'https://apimarket.pedtech.com.br/v1';

  // ============================= Estado ============================= //
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [treeData, setTreeData] = useState([]); // AntD treeData
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Histórico em memória:
  // Map<channelId(string), Set<idCategoriaSaleChannel(string)>>
  const [historicoMap, setHistoricoMap] = useState(new Map());



  // ============================= Helpers ============================= //
  const S = (v) => (v == null ? '' : String(v));

  function toChannelNode(ch) {
    return {
      key: `ch:${ch.salesChannelId}`,
      title: ch.nome || `Canal ${ch.salesChannelId}`,
      isLeaf: false,
      type: 'channel',
      channelId: S(ch.salesChannelId),
      raw: ch,
      children: [], // será preenchido ao expandir
    };
  }

  function toCategoryNode(cat, channelId, channelTitle) {
    return {
      key: `cat:${channelId}:${cat.id}`,
      title: cat.text,
      isLeaf: cat.children === false,
      type: 'category',
      channelId: S(channelId),
      categoryId: S(cat.id),
      channelTitle, // título do canal pai (ex.: "MERCADO LIVRE", "AMAZON")
      raw: cat,
      children: cat.children ? [] : undefined,
    };
  }

  function mergeChildren(list, key, children) {
    return list.map((node) => {
      if (node.key === key) {
        return { ...node, children, isLeaf: !children || children.length === 0 };
      }
      if (node.children) {
        return { ...node, children: mergeChildren(node.children, key, children) };
      }
      return node;
    });
  }

  function markLeaf(list, key) {
    return list.map((n) => {
      if (n.key === key) return { ...n, isLeaf: true, children: undefined };
      if (n.children) return { ...n, children: markLeaf(n.children, key) };
      return n;
    });
  }

  function mapArr(arr, mapper) { return (arr || []).map(mapper); }

  // === confirmação de vínculo + console.log do nó ===
  function handleLinkCategory(node) {
    if (node.type !== 'category') return;

    Modal.confirm({
      title: 'Vincular esta categoria?',
      content: (
        <div>
          <div><b>Canal:</b> {node.channelTitle} <small>(#{node.channelId})</small></div>
          <div><b>Categoria:</b> {node.title} <small>({node.categoryId})</small></div>
        </div>
      ),
      okText: 'Vincular',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          console.log('Vincular categoria ->', {
            canalId: node.channelId,
            canalTitulo: node.channelTitle,
            categoriaId: node.categoryId,
            categoriaTitulo: node.title,
            nodeCompleto: node,
          });

          const bodyVincular = {
            name: node.title,
            alternativeId: categoriaId,     // id da categoria do seu sistema
            fatherAlternativeId: "",
            salesChannelId: node.channelId, // id do canal (ex.: 7 = Mercado Livre)
            salesChannelCatId: node.categoryId, // id da categoria no canal (ex.: MLB1912)
            salesChannel: node.channelTitle
          };

          console.log("bodyVincular", bodyVincular);


          const url = `${API_BASE}/categorias/associacao/${cnpj}`;
          const headers = { token: TOKEN };

          const resp = await axios.post(url, bodyVincular, { headers });

          if (resp.status == 200) {
            message.success('Categoria vinculada com sucesso.');
            voltar();
          }

          // Recarrega histórico/canais para remover da árvore o que já foi vinculado
          await handleReload?.();
        } catch (err) {
          console.error(err);
          message.error('Falha ao vincular a categoria.');
          // Opcional: exibir detalhe do backend, se existir
          // message.error(err?.response?.data?.message || 'Falha ao vincular a categoria.');
        }
      },
    });
  }


  // ============================= API auxiliares ============================= //
  const fetchHistoricoAssociacao = async () => {
    // Variante enviada por você (com categoriaId no path). Se desejar usar somente por CNPJ,
    // troque para: `${API_BASE}/historico-associacao/${cnpj}`
    const url = `${API_BASE}/historico-associacao/${categoriaId}/${cnpj}`;
    const headers = { token: TOKEN };
    const { data } = await axios.get(url, { headers });

    const map = new Map();
    for (const h of (data || [])) {
      const ch = S(h.idSaleChannel);
      const cat = S(h.idCategoriaSaleChannel);
      if (!map.has(ch)) map.set(ch, new Set());
      if (cat) map.get(ch).add(cat);
    }
    setHistoricoMap(map);
    return map;
  }

  async function ensureHistorico() {
    if (historicoMap.size > 0) return historicoMap;
    return await fetchHistoricoAssociacao();
  }

  // ============================= API principais ============================= //
  async function fetchChannels() {
    const hist = await ensureHistorico();
    const canaisComHistorico = new Set([...hist.keys()]); // {"1","7",...}

    const url = `${API_BASE}/saleschannelloja/${cnpj}`;
    const headers = { token: TOKEN };
    const { data } = await axios.get(url, { headers }); // [{ id, cnpj, salesChannelId, nome, sellerId }]

    // Remove canais cujo salesChannelId já está no histórico
    const canaisFiltrados = (data || []).filter(ch =>
      !canaisComHistorico.has(S(ch.salesChannelId))
    );

    return mapArr(canaisFiltrados, toChannelNode);
  }

  async function fetchRootCategories(channelId, channelTitle) {
    await ensureHistorico();
    const url = `${API_BASE}/categorias/canal/${channelId}/${cnpj}`;
    const headers = { token: TOKEN };
    const { data } = await axios.get(url, { headers });

    // Filtra categorias já vinculadas neste canal
    const vinculadas = historicoMap.get(S(channelId)) ?? new Set();
    const filtradas = (data || []).filter(c => !vinculadas.has(S(c.id)));

    return mapArr(filtradas, (c) => toCategoryNode(c, channelId, channelTitle));
  }

  async function fetchChildren(channelId, categoryId, channelTitle) {
    await ensureHistorico();
    const url = `${API_BASE}/categorias/canal/${channelId}/childrens/${categoryId}/${cnpj}`;
    const headers = { token: TOKEN };
    const { data } = await axios.get(url, { headers });

    // Filtra categorias já vinculadas neste canal
    const vinculadas = historicoMap.get(S(channelId)) ?? new Set();
    const filtradas = (data || []).filter(c => !vinculadas.has(S(c.id)));

    return mapArr(filtradas, (c) => toCategoryNode(c, channelId, channelTitle));
  }


  const carregarCanais = useCallback(async () => {
    try {
      setLoading(true);
      await fetchHistoricoAssociacao();
      const channels = await fetchChannels();
      setTreeData(channels);
    } catch (e) {
      console.error(e);
      message.error('Erro ao carregar canais');
    } finally {
      setLoading(false);
    }
  }, []); // <- sem deps, callback estável

  useEffect(() => {
    carregarCanais();
  }, [carregarCanais]);

  // ============================= Lazy load ============================= //
  const onLoadData = async (treeNode) => {
    // Se já tem filhos carregados, não recarrega
    if (treeNode.children && treeNode.children.length > 0) return;

    try {
      if (treeNode.type === 'channel') {
        const children = await fetchRootCategories(treeNode.channelId, treeNode.title);
        if (!children || children.length === 0) {
          // canal sem categorias
          setTreeData((origin) => markLeaf(origin, treeNode.key));
        } else {
          setTreeData((origin) => mergeChildren(origin, treeNode.key, children));
        }
      } else if (treeNode.type === 'category') {
        if (treeNode.isLeaf) return; // nada a carregar
        const children = await fetchChildren(treeNode.channelId, treeNode.categoryId, treeNode.channelTitle);
        if (!children || children.length === 0) {
          // virou folha -> pergunta se quer vincular
          handleLinkCategory(treeNode);
          setTreeData((origin) => markLeaf(origin, treeNode.key));
        } else {
          setTreeData((origin) => mergeChildren(origin, treeNode.key, children));
        }
      }
    } catch (e) {
      console.error(e);
      message.error('Falha ao carregar itens');
    }
  };

  // ============================= Busca local ============================= //
  const filteredTree = useMemo(() => {
    const term = (search || '').toLowerCase().trim();
    if (!term) return treeData;

    const filterRec = (nodes) =>
      (nodes || [])
        .map((n) => {
          const label = String(n.title || '').toLowerCase();
          const selfMatch = label.includes(term);
          const kids = n.children?.length ? filterRec(n.children) : [];
          if (selfMatch || kids.length) return { ...n, children: kids };
          return null;
        })
        .filter(Boolean);

    return filterRec(treeData);
  }, [treeData, search]);

  // ============================= UI Helpers ============================= //
  async function handleReload() {
    try {
      setLoading(true);
      await fetchHistoricoAssociacao(); // atualiza histórico
      const channels = await fetchChannels(); // re-carrega já filtrado
      setTreeData(channels);
      setExpandedKeys([]);
      //message.success('Atualizado');
    } catch {
      message.error('Não foi possível recarregar');
    } finally {
      setLoading(false);
    }
  }

  function expandAll() {
    const keys = [];
    const walk = (nodes) => nodes.forEach((n) => { keys.push(n.key); if (n.children?.length) walk(n.children); });
    walk(treeData);
    setExpandedKeys(keys);
  }

  function collapseAll() {
    setExpandedKeys([]);
  }

  // ============================= Render ============================= //
  return (
    <div style={styles.wrap}>
      <Row gutter={[16, 16]} style={{ height: '100%' }}>
        <Col span={24} style={{ height: '100%' }}>
          <Card
            title={<>
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button type="primary" onClick={voltar} style={{ background: '#000', borderColor: '#000' }}>
                  Voltar
                </Button>
              </div>

              <Space direction="vertical" size={4}>
                <Title level={5} style={{ margin: 0 }}>Canais & Categorias</Title>
                <Text type="secondary">Expanda o canal para carregar as categorias; expanda a categoria para ver subníveis</Text>
              </Space>
            </>}
            extra={
              <Space>
                <Tooltip title="Recarregar canais e histórico">
                  <Button icon={<ReloadOutlined />} onClick={handleReload} />
                </Tooltip>
                <Tooltip title="Expandir tudo (somente nós já carregados)">
                  <Button icon={<NodeExpandOutlined />} onClick={expandAll} />
                </Tooltip>
                <Tooltip title="Colapsar tudo">
                  <Button icon={<NodeCollapseOutlined />} onClick={collapseAll} />
                </Tooltip>
              </Space>
            }
            bodyStyle={{ padding: 12 }}
            style={{ height: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Input
                allowClear
                placeholder="Buscar nos nós já carregados..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <div style={styles.treeBox}>
                {loading ? (
                  <div style={styles.center}><Spin /></div>
                ) : (
                  <Tree
                    showLine={{ showLeafIcon: false }}
                    switcherIcon={<DownOutlined />}
                    loadData={onLoadData}
                    treeData={filteredTree}
                    blockNode
                    expandedKeys={expandedKeys}
                    onExpand={setExpandedKeys}
                    onSelect={(_, info) => {
                      const node = info?.node;
                      // se já é folha (API children:false) -> pergunta no clique
                      if (node?.type === 'category' && node?.isLeaf) {
                        handleLinkCategory(node);
                      }
                    }}
                    titleRender={(node) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{node.title}</span>
                        {node.type === 'channel' ? (
                          <Tag color="blue" style={{ marginLeft: 4 }}>Canal #{node.channelId}</Tag>
                        ) : (
                          <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
                            ({node.categoryId})
                          </Text>
                        )}
                      </div>
                    )}
                  />
                )}
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default NovaCategoriaMkt;

const styles = {
  wrap: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  treeBox: {
    height: '70vh',
    minHeight: 360,
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    padding: 8,
    overflow: 'auto',
    background: '#fff',
  },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' },
};
